import React, { Fragment, useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppContext } from "@component/AppProvider";
import parse, { domToReact } from "html-react-parser";
import swal from "sweetalert";
import moment from "moment";

import _API from "@util/api";
import _U from "@util/utilities";
import _DEF from "@config/defines";
import _CK from "@util/cookie";

import Loading from "@component/UI/Loading";

export default () => {
    const { site, iam } = useContext(AppContext);
    const [cfg, setCfg] = useState(null);
    const [amounts, setAmounts] = useState(null);
    const [awsKey, setAwsKey] = useState(null);
    const [data, setData] = useState({});
    const [category, setCategory] = useState({});
    const [memos, setMemos] = useState([]);

    const [isLoading, setIsLoading] = useState(true);

    const navigate = useNavigate();

    const loadConfig = () => {
        _API.get({ path: "/site/registration/config" })
            .then((res) => {
                const { cfg, options, memos, amounts, reg } = res.data;
                setCfg(cfg);
                setAmounts(amounts);
                setMemos(memos);
                if (reg) {
                    setData({
                        payStatus: reg?.payStatus,
                        idFileIdx: reg?.idFileIdx,
                        amountType: reg?.amountType,
                        fileName: reg?.fileName,
                        signedUrl: reg?.signedUrl,
                    });
                    setCategory({ categoryIdx: reg?.categoryIdx, amount: reg?.amount, isIdCard: reg?.isIdCard });
                }
            })
            .catch((e) => {
                _API.alert({ message: e.message }).then(() => {
                    navigate("/");
                });
            });
    };

    useEffect(() => {
        if (site?.siteCode) {
            loadConfig();
        }
    }, [site?.siteCode]);

    useEffect(() => {
        if (cfg) {
            if (cfg?.isUseEarly !== "Y" && cfg?.isUsePre !== "Y" && cfg?.isUseLate !== "Y") {
                swal("Pre-registration settings have not been completed.").then(() => {
                    navigate("/");
                });
                return;
            }

            if (data?.payStatus === "Y") {
                swal("Please directly contact the secretariat (" + site["emailRegistration"] + ") to modify your registration status.").then(() => {
                    navigate("/");
                });
                return;
            }

            let expire = 0;
            //early
            if (cfg?.isUseEarly === "Y" && moment() >= moment(cfg.earlyStartDate) && moment() < moment(cfg.earlyEndDate)) {
                expire++;
                if (!data?.amountType) setData({ ...data, amountType: "EARLY" });
            }

            //regular
            if (expire == 0 && cfg?.isUsePre === "Y" && moment() >= moment(cfg.preStartDate) && moment() < moment(cfg.preEndDate)) {
                expire++;
                if (!data?.amountType) setData({ ...data, amountType: "PRE" });
            }

            //onsite
            if (expire == 0 && cfg?.isUseLate === "Y" && moment() >= moment(cfg.lateStartDate) && moment() < moment(cfg.lateEndDate)) {
                expire++;
                if (!data?.amountType) setData({ ...data, amountType: "LATE" });
            }

            if (expire <= 0) {
                swal("This is not the registration period.").then(() => {
                    navigate("/");
                });
                return;
            }

            setIsLoading(false);
        }
    }, [cfg, data]);

    const getPrice = (item) => {
        if (data?.amountType === "EARLY") {
            return item.earlyAmount;
        }
        if (data?.amountType === "PRE") {
            return item.regularAmount;
        }
        if (data?.amountType === "LATE") {
            return item.onsiteAmount;
        }

        return 0;
    };

    const isIdCard = () => {
        return category?.isIdCard === "Y";
    };

    const onFileUpload = (e) => {
        setIsLoading(true);
        const file = e.target.files[0];

        if (!file) {
            swal("파일을 선택해 주세요.");
            return;
        }

        const realFileName = `${site?.siteCode}_${iam?.id}_idcard`;

        const req = { aws: awsKey, file, siteCode: site?.siteCode, realFileName };
        _API.upload(req)
            .then((res) => res.data)
            .then((file) => {
                setData({ ...data, idFileIdx: file?.id });
                setIsLoading(false);
            })
            .catch((e) => {
                swal(e.message);
            });
    };

    const loadPrepare = () => {
        _API.loadUploadKeys(true)
            .then((res) => res.data)
            .then((data) => setAwsKey(data));
    };

    useEffect(() => {
        loadPrepare();
    }, []);

    const onCategoryChange = (category) => {
        setCategory({ categoryIdx: category.idx, amount: getPrice(category), isIdCard: category?.isIdCard || "N" });
    };

    const onNext = () => {
        if (!category?.categoryIdx) {
            swal("Please choose your category");
            return;
        }

        if (isIdCard() && !data?.idFileIdx) {
            swal("Please choose your id card.");
            return;
        }

        _API.post({ path: "/site/registration/step1", data: { data, category, registrationIdx: cfg?.idx } })
            .then((res) => {
                if (res.error === 0) {
                    if (cfg?.isUseOptionProgram !== "Y" && cfg?.isUseSpecialDietary !== "Y") {
                        navigate("/registration/regist/step3");
                    } else {
                        navigate("/registration/regist/step2");
                    }
                }
            })
            .catch((e) => {
                console.log(e);

                _API.alert({ message: e.message });
            });
    };

    const getMemo = (type) => {
        let memo = null;
        if (type === "REG" && iam?.country === "KR") {
            memo = memos?.find((m) => m.type === "REG_KR");
        }
        if (type === "REG" && iam?.country !== "KR") {
            memo = memos?.find((m) => m.type === "REG_OTHERS");
        }

        if (!memo?.content) {
            return "";
        }
        return <div dangerouslySetInnerHTML={{ __html: memo?.content }}></div>;
    };

    if (!_CK.isLogin()) {
        swal("Please Login first.").then(() => {
            document.location.href = "/";
        });
        return;
    }

    return (
        <Fragment>
            {isLoading && <Loading />}
            <div className="contents">
                {site?.naviRegistration && parse(site?.naviRegistration)}

                <div className="right">
                    <div className="contentsTop">
                        <h3>Online Registration</h3>
                        <div className="location">
                            <a href="/" className="home">
                                home
                            </a>
                            <div className="loc_txt">
                                <span>Online Registration</span>
                            </div>
                        </div>
                    </div>
                    <div className="contWrap">
                        <h4 className="title">Registration Category</h4>
                        {cfg?.isUseSocietyMember === "Y" && <h5>{cfg?.isSocietyMember === "Y" ? "MEMBERS" : "Non-MEMBERS"}</h5>}
                        <p>Please select your category for the registration. </p>

                        <div className="boxTable">
                            <table className="online_reg_step01">
                                <thead>
                                    <tr>
                                        <th width="25%">Category</th>
                                        {data?.amountType === "EARLY" && (
                                            <th>
                                                Early Registration
                                                <br />({moment(cfg?.earlyStartDate).format("MMM. DD, YYYY")} ~ {moment(cfg?.earlyEndDate).format("MMM. DD, YYYY")})
                                            </th>
                                        )}
                                        {data?.amountType === "PRE" && (
                                            <th>
                                                Regular Registration
                                                <br />({moment(cfg?.preStartDate).format("MMM. DD, YYYY")} ~ {moment(cfg?.preEndDate).format("MMM. DD, YYYY")})
                                            </th>
                                        )}
                                        {data?.amountType === "LATE" && (
                                            <th>
                                                On-site Registration
                                                <br />({moment(cfg?.lateStartDate).format("MMM. DD, YYYY")} ~ {moment(cfg?.lateEndDate).format("MMM. DD, YYYY")})
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {amounts?.map((item, index) => (
                                        <tr key={index}>
                                            <td className="tit">{item.category}</td>

                                            <td>
                                                <label>
                                                    <input type="radio" name="category" onChange={() => onCategoryChange(item)} checked={item.idx === category?.categoryIdx} />
                                                    &nbsp;
                                                    {item.lang == "USD" && `${item.lang} `}
                                                    {_U.addComma(getPrice(item))}
                                                    {item.lang == "WON" && " 원"}
                                                </label>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td className="total">Sub Total</td>
                                        <td className="total" colspan="3">
                                            합계 {iam?.country !== "KR" && `USD `}
                                            {_U.addComma(category?.amount || 0)}
                                            {iam?.country === "KR" && ` 원`}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {isIdCard() && (
                            <>
                                <h5>Upload Your Student ID </h5>

                                <div className="boxgray">
                                    * Upload Your Student ID (JPG, PDF, GIF) <input type="file" onChange={onFileUpload} />
                                    {data?.fileName && (
                                        <div>
                                            Attachment :{" "}
                                            <a href={data?.signedUrl} target="_blank">
                                                {data?.fileName}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {getMemo("REG")}

                        <div className="btnArea1">
                            <button className="btn1 btnBlue1" type="button" onClick={onNext}>
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};
